import {Dropdown, Text} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {Constants} from '@/utils/constants/commonConstants'
import {ANY_BUY_OPTION, ANY_ID} from '@/utils/constants/promotionConstants'
import {useMemo, useState} from 'react'
import PromotionSelectionModal from '../PromotionSelectionModal'
import {RewardItem} from './Rewards/RewardItem'

const defaultCondition = {
  type: 'BUY',
  items: [],
}

export const BuyMultipleProductsCondition = ({promotionCondition}) => {
  const {updatePromotion, promotionConfig, isLoadingConfig} = usePromotion()
  const [modalProps, setModalProps] = useState({
    show: false,
    type: '',
    index: 0,
  })

  const buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )

  const buyTypeOptions = promotionConfig?.conditions?.itemTypeOptions ?? []

  if (!promotionCondition) {
    return null
  }

  const [rawCond1, rawCond2] = promotionCondition.conditionGroup?.conditions || []
  const cond1 = rawCond1 ?? defaultCondition
  const cond2 = rawCond2 ?? defaultCondition

  const handleBuyCount = (values, index = 0) => {
    const value = values?.[0]?.id
    if (value) {
      // If ANY is selected in the quantity dropdown, set quantity to 1
      if (isNaN(value) && value === ANY_ID) {
        updateConditionByIndex({quantity: 1}, index)
      } else {
        updateConditionByIndex({quantity: Number(value)}, index)
      }
    }
  }

  const updateConditionByIndex = (update, conditionIndex = 0) => {
    updatePromotion(prev => {
      // Deep clone the promotionConditions array to prevent reference issues
      const updatedConditions = prev.promotionConditions.map(condition => {
        // If not the target sequence, return a deep clone of the condition
        if (condition.sequence !== promotionCondition.sequence) {
          return JSON.parse(JSON.stringify(condition))
        }

        // Deep clone the condition before updating
        const clonedCondition = JSON.parse(JSON.stringify(condition))

        // Update the condition at the specified index
        if (clonedCondition.conditionGroup?.conditions?.[conditionIndex]) {
          clonedCondition.conditionGroup.conditions[conditionIndex] = {
            ...clonedCondition.conditionGroup.conditions[conditionIndex],
            ...update,
          }
        }

        return clonedCondition
      })

      return {
        ...prev,
        promotionConditions: updatedConditions,
      }
    })
  }

  const clearValues = index => {
    updateConditionByIndex({items: [], itemType: null}, index)
  }

  const handleSelectedBuyItem = (values, index) => {
    if (!values || !values.length) {
      clearValues(index)
      return
    }

    const isModalValue = values.find(item => buyTypeOptions.find(({id}) => id === item.id))

    if (isModalValue) {
      if (isModalValue.id === ANY_ID) {
        handleValues({values: [ANY_BUY_OPTION], itemType: ANY_ID}, null, index)
      } else {
        setModalProps({show: true, type: isModalValue.id, index})
      }
    } else {
      // Directly update when user adds/removes regular items
      const currentItemType = index === 0 ? cond1.itemType : cond2.itemType
      handleValues({values, itemType: currentItemType}, null, index)
    }
  }

  const handleCloseModal = () => {
    setModalProps(prev => ({...prev, show: false}))
  }

  // console.log(cond1, cond2)
  const handleValues = (selectedItemsAndType, productCombination, index = modalProps.index) => {
    const {values, itemType} = selectedItemsAndType || {}
    if (!values || !Array.isArray(values)) {
      clearValues(index)
      return
    }
    const items = values.map(item => ({id: item?.id, name: item?.displayText}))

    updateConditionByIndex(
      {
        items,
        productCombination,
        itemType,
      },
      index
    )
  }

  const handleSubmitModal = (selectedItems, combination) => {
    handleValues(selectedItems, combination)
    handleCloseModal()
  }

  const buyCount1 = cond1.quantity
  const items1 = cond1?.items ?? []

  const selectedValuesText1 = items1.map(item => item.name).join(', ')
  const quantityValue1 = buyQtyOptions?.filter(item => item.id === buyCount1) ?? []
  const productsValue1 = items1.map(item => ({id: item.id, displayText: item.name})) ?? []

  const buyCount2 = cond2.quantity
  const items2 = cond2?.items ?? []

  const selectedValuesText2 = items2.map(item => item.name).join(', ')
  const quantityValue2 = buyQtyOptions?.filter(item => item.id === buyCount2) ?? []
  const productsValue2 = items2.map(item => ({id: item.id, displayText: item.name})) ?? []

  const productsValue1HasAny = productsValue1?.find(value => value.id === ANY_ID)
  const butyQtyOptions1 = productsValue1HasAny ? buyQtyOptions?.filter(item => item.id !== ANY_ID) : buyQtyOptions

  const quantityValue1HasAny = quantityValue1?.find(value => value.id === ANY_ID)
  const buyTypeOptions1 = quantityValue1HasAny ? buyTypeOptions?.filter(item => item.id !== ANY_ID) : buyTypeOptions

  const productsValue2HasAny = productsValue2?.find(value => value.id === ANY_ID)
  const butyQtyOptions2 = productsValue2HasAny ? buyQtyOptions?.filter(item => item.id !== ANY_ID) : buyQtyOptions

  const quantityValue2HasAny = quantityValue2?.find(value => value.id === ANY_ID)
  const buyTypeOptions2 = quantityValue2HasAny ? buyTypeOptions?.filter(item => item.id !== ANY_ID) : buyTypeOptions
  return (
    <>
      <Text>Buy</Text>
      <Dropdown
        isJsonWrap={false}
        childList={butyQtyOptions1}
        placeholder={ANY_ID}
        updateValue={values => handleBuyCount(values, 0)}
        isClearable={false}
        value={quantityValue1}
        autocomplete="off"
        style={{width: 84}}
        loading={isLoadingConfig}
      />
      <Dropdown
        isJsonWrap={false}
        childList={buyTypeOptions1}
        placeholder={selectedValuesText1 || Constants.AddProducts}
        value={[...productsValue1]}
        updateValue={values => handleSelectedBuyItem(values, 0)}
        selection="multiple"
        isClearable={true}
        autocomplete="off"
        loading={isLoadingConfig}
      />
      <Text>{'and buy'}</Text>
      <Dropdown
        isJsonWrap={false}
        childList={butyQtyOptions2}
        placeholder={ANY_ID}
        updateValue={values => handleBuyCount(values, 1)}
        isClearable={false}
        value={quantityValue2}
        autocomplete="off"
        style={{width: 84}}
        loading={isLoadingConfig}
      />
      <Dropdown
        isJsonWrap={false}
        childList={buyTypeOptions2}
        placeholder={selectedValuesText2 || Constants.AddProducts}
        value={[...productsValue2]}
        updateValue={values => handleSelectedBuyItem(values, 1)}
        selection="multiple"
        isClearable={true}
        autocomplete="off"
        loading={isLoadingConfig}
      />
      <Text>{'and get'}</Text>
      <RewardItem promotionCondition={promotionCondition} />
      <PromotionSelectionModal
        key={JSON.stringify(modalProps)}
        show={modalProps.show}
        type={modalProps.type}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        conditionValues={{
          values: modalProps.index === 0 ? productsValue1 : productsValue2,
          itemType: modalProps.index === 0 ? cond1.itemType : cond2.itemType,
          productCombination: modalProps.index === 0 ? cond1.productCombination : cond2.productCombination,
        }}
      />
    </>
  )
}
